import type {
  AnnotationInteraction,
  AnnotationTrigger,
  AnnotationTriggerEffect,
  AnnotationStateRecord,
} from '../types/worldAnnotation';
import { cloneAnnotationConditionGroup } from './worldAnnotationConditions';
import {
  sanitizeToken,
  type AnnotationRuntimeStateById,
} from './worldAnnotationRuntimeNormalizers';

export const cloneAnnotationStateRecord = (
  value: AnnotationStateRecord | undefined,
): AnnotationStateRecord | undefined => {
  if (!value) {
    return undefined;
  }
  return Object.fromEntries(Object.entries(value).map(([key, entry]) => [key, entry]));
};

export const cloneAnnotationRuntimeStateById = (
  value: AnnotationRuntimeStateById | undefined,
): AnnotationRuntimeStateById | undefined => {
  if (!value) {
    return undefined;
  }

  const entries = Object.entries(value)
    .map(
      ([annotationId, record]) =>
        [sanitizeToken(annotationId), cloneAnnotationStateRecord(record)] as const,
    )
    .filter(
      (entry): entry is [string, AnnotationStateRecord] => Boolean(entry[0]) && Boolean(entry[1]),
    );

  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
};

export const cloneAnnotationInteraction = (
  interaction: AnnotationInteraction,
): AnnotationInteraction => ({
  ...interaction,
  ...(interaction.effectPayload &&
  typeof interaction.effectPayload === 'object' &&
  !Array.isArray(interaction.effectPayload)
    ? { effectPayload: { ...interaction.effectPayload } }
    : {}),
  ...(interaction.requiredState
    ? { requiredState: cloneAnnotationStateRecord(interaction.requiredState) }
    : {}),
  ...(interaction.stateChange
    ? { stateChange: cloneAnnotationStateRecord(interaction.stateChange) }
    : {}),
});

export const cloneAnnotationInteractions = (
  interactions: readonly AnnotationInteraction[] | undefined,
): AnnotationInteraction[] | undefined =>
  Array.isArray(interactions) ? interactions.map(cloneAnnotationInteraction) : undefined;

const cloneAnnotationTriggerEffect = (effect: AnnotationTriggerEffect): AnnotationTriggerEffect => {
  switch (effect.type) {
    case 'animation':
      return {
        ...effect,
        payload: {
          ...effect.payload,
        },
      };
    case 'sound':
      return {
        ...effect,
        payload: {
          ...effect.payload,
        },
      };
    case 'emote':
      return {
        ...effect,
        payload: {
          ...effect.payload,
        },
      };
    case 'event':
      return {
        ...effect,
        payload: {
          ...effect.payload,
          ...(effect.payload.detail
            ? { detail: cloneAnnotationStateRecord(effect.payload.detail) }
            : {}),
        },
      };
    case 'state_change':
      return {
        ...effect,
        payload: {
          state: cloneAnnotationStateRecord(effect.payload.state) ?? {},
        },
      };
  }
};

export const cloneAnnotationTrigger = (trigger: AnnotationTrigger): AnnotationTrigger => ({
  ...trigger,
  ...(trigger.eventName ? { eventName: trigger.eventName } : {}),
  ...(trigger.conditions ? { conditions: cloneAnnotationConditionGroup(trigger.conditions) } : {}),
  effect: cloneAnnotationTriggerEffect(trigger.effect),
});

export const cloneAnnotationTriggers = (
  triggers: readonly AnnotationTrigger[] | undefined,
): AnnotationTrigger[] | undefined =>
  Array.isArray(triggers) ? triggers.map(cloneAnnotationTrigger) : undefined;
